import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// QQ互联配置
export const QQ_CONFIG = {
  appId: '102817050',
  redirectUri: 'https://www.lankastudio.cn/Home',
  // 账号后端地址
  backendUrl: 'https://www.lankastudio.cn/accountApi'
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // 初始化：从 localStorage 恢复登录状态
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken) {
      setToken(savedToken);
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          console.error('解析用户信息失败', e);
          // 如果解析失败，尝试从服务器获取
          fetchUserInfo(savedToken);
        }
      } else {
        // 有token但没有用户信息，从服务器获取
        fetchUserInfo(savedToken);
      }
    }
    setLoading(false);
  }, []);

  // QQ登录跳转
  const loginWithQQ = () => {
    const redirectUri = encodeURIComponent(QQ_CONFIG.redirectUri);
    const authUrl = `https://graph.qq.com/oauth2.0/authorize?response_type=code&client_id=${QQ_CONFIG.appId}&redirect_uri=${redirectUri}&state=qq&scope=get_user_info`;
    window.location.href = authUrl;
  };

  // 处理QQ登录回调
  const handleQQCallback = async (code) => {
    try {
      console.log('发送登录请求，code:', code.slice(0, 10) + '...');
      
      const res = await fetch(`${QQ_CONFIG.backendUrl}/qq-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      const data = await res.json();
      console.log('登录响应:', data);

      if (res.ok && data.token) {
        // 保存token
        setToken(data.token);
        localStorage.setItem('token', data.token);

        // 保存用户信息
        if (data.user) {
          const userInfo = {
            id: data.user.id,
            openid: data.user.openid,
            nickname: data.user.nickname,
            avatar: data.user.avatar || data.user.figureurl_qq_2 || data.user.figureurl_2,
            username: data.user.username,
            email: data.user.email,
            hasPassword: data.user.hasPassword,
            // 保存原始QQ头像URL
            figureurl_qq_2: data.user.figureurl_qq_2,
            figureurl_2: data.user.figureurl_2
          };
          setUser(userInfo);
          localStorage.setItem('user', JSON.stringify(userInfo));
        }

        return { success: true };
      } else {
        return { success: false, error: data.message || '登录失败' };
      }
    } catch (error) {
      console.error('登录请求异常:', error);
      return { success: false, error: error.message };
    }
  };

  // 获取用户信息
  const fetchUserInfo = async (authToken = token) => {
    if (!authToken) return;

    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/user-info`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (res.ok) {
        const userData = await res.json();
        const userInfo = {
          id: userData.id,
          openid: userData.openid,
          nickname: userData.nickname,
          avatar: userData.avatar,
          username: userData.username,
          email: userData.email,
          hasPassword: userData.hasPassword
        };
        setUser(userInfo);
        localStorage.setItem('user', JSON.stringify(userInfo));
      } else if (res.status === 401) {
        // Token 无效，清除登录状态
        logout();
      }
    } catch (error) {
      console.error('获取用户信息失败', error);
    }
  };

  // 更新用户信息
  const updateUserInfo = async (updateData) => {
    if (!token) return { success: false, error: '未登录' };

    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/user-info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setUser(prev => ({ ...prev, ...data.user }));
          localStorage.setItem('user', JSON.stringify({ ...user, ...data.user }));
        }
        return { success: true };
      } else {
        const data = await res.json();
        return { success: false, error: data.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // 邮箱登录
  const loginWithEmail = async (email, password) => {
    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password }),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        setToken(data.token);
        localStorage.setItem('token', data.token);

        if (data.user) {
          const userInfo = {
            id: data.user.id,
            nickname: data.user.nickname,
            avatar: data.user.avatar,
            username: data.user.username,
            email: data.user.email,
            hasPassword: data.user.hasPassword
          };
          setUser(userInfo);
          localStorage.setItem('user', JSON.stringify(userInfo));
        }

        return { success: true };
      } else {
        return { success: false, error: data.message || '登录失败' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // 邮箱注册
  const registerWithEmail = async (email, password, nickname) => {
    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password, nickname }),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        setToken(data.token);
        localStorage.setItem('token', data.token);

        if (data.user) {
          const userInfo = {
            id: data.user.id,
            nickname: data.user.nickname || nickname,
            avatar: data.user.avatar,
            username: data.user.username,
            email: email,
            hasPassword: data.user.hasPassword
          };
          setUser(userInfo);
          localStorage.setItem('user', JSON.stringify(userInfo));
        }

        return { success: true };
      } else {
        return { success: false, error: data.message || '注册失败' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // 登出
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // 检查是否已登录
  const isAuthenticated = !!token;

  // 设置/修改密码（QQ 等第三方登录用户可先设置密码）
  const updatePassword = async ({ currentPassword, newPassword }) => {
    if (!token) return { success: false, error: '未登录' };

    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/user-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await res.json();
      if (res.ok) {
        if (data.user) {
          const mergedUser = { ...user, ...data.user };
          setUser(mergedUser);
          localStorage.setItem('user', JSON.stringify(mergedUser));
        }
        return { success: true, message: data.message || '密码更新成功' };
      }

      return { success: false, error: data.message || '密码更新失败' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // 绑定/修改邮箱
  const bindEmail = async (email) => {
    if (!token) return { success: false, error: '未登录' };

    try {
      const res = await fetch(`${QQ_CONFIG.backendUrl}/bind-email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      });

      const data = await res.json();
      if (res.ok) {
        if (data.user) {
          const mergedUser = { ...user, ...data.user };
          setUser(mergedUser);
          localStorage.setItem('user', JSON.stringify(mergedUser));
        }
        return { success: true, message: data.message || '邮箱绑定成功' };
      }
      return { success: false, error: data.message || '邮箱绑定失败' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    loginWithQQ,
    loginWithEmail,
    registerWithEmail,
    updatePassword,
    bindEmail,
    handleQQCallback,
    fetchUserInfo,
    updateUserInfo,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// 自定义Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
